import Image from '@base/Image';
import FutureImage from '@base/FutureImage';
import styled from '@emotion/styled';
import { useEffect } from 'react';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import { segment } from '@actions/analytics';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import useOfflineCentreStore from '@stores/city-specific-offline-centres';
import { getIsLoggedIn } from '@api/selectors/auth';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import {
  OFFLINE_APPLY_SCHOLARSHIP_CLICKED,
  OFFLINE_VIEW_SCHOLARSHIP_DETAILS
} from '@cont/OfflineCentres/constants';
import useCentreInfo from '@api/hooks/citySpecificOfflineCentres/useCentreInfo';
import { useRouter } from 'next/router';
import useWindowSize from '@hooks/useWindowSize';
import LottieAnimation from '@base/LottieAnimation/LottieAnimation';
import { getAnimationPath } from '@utils/animationHelpers';
import Typography from 'aquilla/core/atoms/Typography';
import Button from 'aquilla/core/molecules/Button';

const AnimationContainer = styled(LottieAnimation)`
  width: ${Spacings.SPACING_10B};
  height: ${Spacings.SPACING_4B};
  ${mediaQuery.uptoMobile} {
    width: ${Spacings.SPACING_20B} !important;
    height: ${Spacings.SPACING_8B};
  }
`;

const AnimationFallBackImage = ({ imageURL }) => {
  return <Image src={imageURL} width={80} height={80} alt="doubt-state-card" />;
};

const TrophyImageDiv = styled.div`
  height: 100%;
  width: 100%;
  ${mediaQuery.uptoMobile} {
    padding: ${Spacings.SPACING_6B} ${Spacings.SPACING_4B}
      ${Spacings.SPACING_0B} ${Spacings.SPACING_0B};
  }
`;

const Container = styled.div`
  border-radius: ${Spacings.SPACING_4B};
  height: 396px;
  display: flex;
  justify-content: space-around;
  background-color: var(--color-purple-secondary);
  margin: auto;
  margin-bottom: ${Spacings.SPACING_0B};
  margin-top: ${Spacings.SPACING_0B};
  width: 1136px;
  ${mediaQuery.uptoMobile} {
    height: fit-content;
    margin-bottom: ${Spacings.SPACING_0B};
    margin-top: ${Spacings.SPACING_0B};
    padding: 0px ${Spacings.SPACING_4B} ${Spacings.SPACING_0B}
      ${Spacings.SPACING_4B};
    flex-direction: column-reverse;
    width: 100%;
    border-radius: ${Spacings.SPACING_0B};
    border-bottom: solid ${Spacings.SPACING_2B} var(--color-violet-1);
  }
`;

const HeaderDiv = styled.div`
  margin-bottom: ${Spacings.SPACING_2B};
  display: flex;
`;

const LeftDiv = styled.div`
  padding: ${Spacings.SPACING_12B} ${Spacings.SPACING_0B} ${Spacings.SPACING_0B}
    ${Spacings.SPACING_12B};
  display: flex;
  height: 396px;
  max-width: 704px;
  flex-direction: column;
  ${mediaQuery.uptoMobile} {
    height: auto;
    padding: ${Spacings.SPACING_8B} ${Spacings.SPACING_0B}
      ${Spacings.SPACING_8B} ${Spacings.SPACING_0B};
    width: 100%;
    position: relative;
  }
`;
const RightDiv = styled.div`
  width: 356px;
  height: 396px;
  ${mediaQuery.uptoMobile} {
    width: auto;
    height: auto;
    position: absolute;
    right: 0;
  }
`;

const UpperDiv = styled.div`
  ${mediaQuery.uptoMobile} {
    width: 70%;
  }
`;
const ScholarShipData = styled.div`
  margin-bottom: ${Spacings.SPACING_6B};
  margin-top: ${Spacings.SPACING_4B};
  display: flex;
  align-items: center;
  margin-right: ${Spacings.SPACING_1B};
  ${mediaQuery.uptoMobile} {
    margin-bottom: ${Spacings.SPACING_0B};
  }
`;

const StyH3 = styled(Typography)`
  margin-right: ${Spacings.SPACING_1B};
`;
const StyP1 = styled(Typography)`
  color: var(--color-text-secondary);
`;
const Stats = styled(Typography)`
  margin-left: ${Spacings.SPACING_2B};
  font-weight: ${Fonts.SEMIBOLD};
`;

const ButtonContainer = styled.div`
  margin-top: ${Spacings.SPACING_7B};
  display: flex;
`;
const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  ${mediaQuery.uptoMobile} {
    flex-direction: column-reverse;
  }
`;
const ApplyButton = styled(Button)`
  margin-right: ${Spacings.SPACING_4B};
  width: 200px;
  padding: ${Spacings.SPACING_14} ${Spacings.SPACING_0B};
  ${mediaQuery.uptoMobile} {
    margin-right: ${Spacings.SPACING_3B};
    font-size: ${Spacings.SPACING_3B};
    padding: ${Spacings.SPACING_0B};
  }
`;
const ViewScholarship = styled(Button)`
  width: 200px;
  ${mediaQuery.uptoMobile} {
    font-size: ${Spacings.SPACING_3B};
    padding: 0;
  }
`;

const InnerContainer = styled.div`
  width: ${Spacings.APP_CONTAINER_WIDTH};
  display: flex;
  justify-content: space-between;
  ${mediaQuery.uptoMobile} {
    height: fit-content;
    padding: 0px ${Spacings.SPACING_0B} ${Spacings.SPACING_0B}
      ${Spacings.SPACING_0B};
    width: 100% !important;
  }
`;

const DescriptionWrapper = styled.div`
  margin-top: ${Spacings.SPACING_5B};
  display: flex;
  flex-wrap: wrap;
  row-gap: ${Spacings.SPACING_4B};
  ${mediaQuery.uptoMobile} {
    row-gap: ${Spacings.SPACING_3B};
  }
`;
const DescriptionImage = styled(FutureImage)`
  width: 24px;
  height: 24px;
`;

const Description = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  width: 324px;
  row-gap: ${Spacings.SPACING_4B};
  ${mediaQuery.uptoMobile} {
    row-gap: ${Spacings.SPACING_3B};
  }
`;

const DescriptionText = styled(Typography)`
  margin-left: ${Spacings.SPACING_6};
`;

const StatHeading = styled(Typography)``;
const StatSubHeading = styled(Typography)`
  color: var(--color-text-secondary);
`;

const Scholarship = ({ data }) => {
  const {
    header,
    subHeader,
    subHeaderApp,
    wonText,
    learnersText,
    cta1,
    cta2,
    cta1DeepLinkUrl,
    cta2DeepLinkUrl,
    descriptionCtAs
  } = data;
  const learnerScholarShipData = `${learnersText} have ${wonText}`;
  const {
    query: { goalUID: routeGoalUID, centreUID }
  } = useRouter();
  const { centreInfo } = useCentreInfo(routeGoalUID, centreUID);
  const { centreName, cityName } = centreInfo || {};
  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const { isScholarShipBlockClicked, setScholarShipBlockClicked } =
    useOfflineCentreStore();

  const applyNowHandler = () => {
    segment.track(OFFLINE_APPLY_SCHOLARSHIP_CLICKED, {
      goal_uid: routeGoalUID,
      centre_name: centreName,
      city: cityName
    });
    window.open(cta1DeepLinkUrl);
  };

  useEffect(() => {
    if (isLoggedIn) {
      if (isScholarShipBlockClicked) {
        setScholarShipBlockClicked(false);
        segment.track(OFFLINE_VIEW_SCHOLARSHIP_DETAILS, {
          goal_uid: routeGoalUID,
          centre_name: centreName,
          city: cityName
        });
        // to change
        window.open(cta2DeepLinkUrl);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const viewDetailsHandler = () => {
    if (!isLoggedIn) {
      setScholarShipBlockClicked(true);
      openLoginDrawer(true);
      return;
    }
    segment.track(OFFLINE_VIEW_SCHOLARSHIP_DETAILS, {
      goal_uid: routeGoalUID,
      centre_name: centreName,
      city: cityName
    });
    // to change
    window.open(cta2DeepLinkUrl);
  };
  const { isMobile } = useWindowSize();
  const loadingStateAnimation = (
    <AnimationContainer
      Fallback={
        <AnimationFallBackImage imageURL="offline-centres/membersAttempting.svg" />
      }
      src={getAnimationPath('offlineCentres/JoiningMembers.json')}
    />
  );
  return (
    <Container>
      <InnerContainer>
        <LeftDiv>
          <UpperDiv>
            <HeaderDiv>
              <StyH3 variant={isMobile ? 'h4' : 'h2'}>{header}</StyH3>
              {!isMobile && (
                <Image
                  src="/offline-centres/scholarship-3-stars.svg"
                  width={24}
                  height={24}
                  alt="3-stars"
                />
              )}
            </HeaderDiv>
            <StyP1 variant="p1">{isMobile ? subHeaderApp : subHeader}</StyP1>
          </UpperDiv>
          <DescriptionWrapper>
            {descriptionCtAs?.map((desc) => {
              return (
                <Description key={desc}>
                  <DescriptionImage
                    alt="check-mark"
                    width={24}
                    height={24}
                    src="/offlineCentres/scholarship_checkmark.svg"
                  />
                  <DescriptionText variant={isMobile ? 'p2' : 'p1'}>
                    {desc}
                  </DescriptionText>
                </Description>
              );
            })}
          </DescriptionWrapper>
          <ButtonWrapper>
            <ButtonContainer>
              <ApplyButton
                label={cta1 || 'Apply now'}
                onClick={applyNowHandler}
                dataAnalytics="subscribe-selectLite"
                variant="filled"
                color="black"
                size={isMobile ? 'medium' : 'large'}
              />
              <ViewScholarship
                label={cta2 || 'View scholarship details'}
                onClick={viewDetailsHandler}
                dataAnalytics="subscribe-selectLite"
                variant="secondary"
                color="black"
                size={isMobile ? 'medium' : 'large'}
              />
            </ButtonContainer>
            <ScholarShipData>
              {loadingStateAnimation}
              {isMobile ? (
                <Stats>
                  <StatHeading variant="h5"> {learnersText}</StatHeading>
                  <StatSubHeading variant="p3"> {wonText}</StatSubHeading>
                </Stats>
              ) : (
                <Stats variant="p2">{learnerScholarShipData}</Stats>
              )}
            </ScholarShipData>
          </ButtonWrapper>
        </LeftDiv>
        <RightDiv>
          <TrophyImageDiv>
            <Image
              src={
                isMobile
                  ? '/offline-centres/ScholarshipTrophy.svg'
                  : '/offline-centres/scholorship-Illustration.svg'
              }
              alt="hero-image"
              height={isMobile ? '63' : '396'}
              width={isMobile ? '94' : '356'}
            />
          </TrophyImageDiv>
        </RightDiv>
      </InnerContainer>
    </Container>
  );
};

export default Scholarship;
